<template>
  <div class="fts-field-design" :class="{ 'field-design--expand': expanded }">
    <fts-input-group placement="start" class="field-design-label">
      <fts-form-label>
        <div class="field-design-label-name">
          {{ item.displayName || "(untitled)" }}
        </div>
        <div class="field-design-label-description" v-if="item.description">
          {{ item.description }}
        </div>
      </fts-form-label>
      <fts-input-group-icon
        slot="append"
        @click.native="toggleExpand"
        v-if="isFields && expanded"
      >
        <font-awesome-icon :icon="['fas', 'caret-down']" />
      </fts-input-group-icon>
    </fts-input-group>

    <div class="field-design-control">
      <template v-if="isFields && expanded">
        <fts-field-design
          v-for="fieldItem in item.fields"
          :key="fieldItem.fieldName"
          :variables="variables"
          :item="fieldItem"
          v-model="mutableValue[fieldItem.fieldName]"
          @formula:change="onFormulaChange"
        />
      </template>

      <fts-input-group v-else>
        <fts-formula
          v-model="mutableValue.formula"
          :variables="variables"
          class="field-design-formula"
          @change="onFormulaChange"
          v-if="isAdvanced"
        />
        <!-- boolean -->
        <fts-checkbox
          v-model="mutableValue"
          class="field-design-form-control field-design-checkbox"
          v-else-if="item.type === 'boolean'"
        />
        <!-- date -->
        <fts-date-time
          v-model="mutableValue"
          :min-datetime="item.minDatetime"
          :max-datetime="item.maxDatetime"
          :predefined-format-id="item.predefinedFormatId"
          class="field-design-form-control"
          v-else-if="item.type === 'date'"  
        />
        <!-- number -->
        <fts-number-currency
          :numeral-integer-scale="item.numeralIntegerScale"
          :numeral-decimal-scale="item.numeralDecimalScale"
          :numeral-positive-only="item.numeralPositiveOnly"
          v-model="mutableValue"
          v-else-if="item.type === 'number'"
        />
        <!-- select -->
        <fts-select
          :options="item.options"
          v-model="mutableValue"
          class="field-design-form-control"
          v-else-if="item.type === 'select'"
        />
        <!-- default -->
        <input v-model="mutableValue" class="fts-form-control" v-else />

        <template #append>
          <fts-input-group-icon
            :active="isAdvanced"
            @click.native="toggleAdvanced"
            v-if="!isFields || (isFields && !expanded)"
          >
            <i class="fts-icon-formula"></i>
          </fts-input-group-icon>
          <fts-input-group-icon @click.native="toggleExpand" v-if="isFields">
            <font-awesome-icon :icon="['fas', 'caret-right']" />
          </fts-input-group-icon>
        </template>
      </fts-input-group>
    </div>
  </div>
</template>

<script>
import FTSFormula from "../FTSFormula";
import FTSFormLabel from "../../elements/FTSFormLabel";
import FTSInputGroup from "../../elements/input-group/FTSInputGroup";
import FTSCheckbox from "../../elements/FTSCheckbox";
import FTSDateTime from "../../elements/FTSDateTime";
import FTSNumberCurrency from "../../elements/FTSNumberCurrency";
import FTSSelect from "../../elements/FTSSelect";

export default {
  name: "fts-field-design",
  components: {
    "fts-formula": FTSFormula,
    "fts-form-label": FTSFormLabel,
    "fts-input-group": FTSInputGroup,
    "fts-checkbox": FTSCheckbox,
    "fts-date-time": FTSDateTime,
    "fts-number-currency": FTSNumberCurrency,
    "fts-select": FTSSelect
  },
  props: {
    variables: Array,
    value: {
      required: true,
    },
    item: Object,
    validator: Object,
    expanded: Boolean,
  },
  model: {
    prop: "value",
    event: "updateValue",
  },
  methods: {
    toggleAdvanced() {
      this.mutableValue = this.isAdvanced ? null : { formula: null };
    },
    toggleExpand() {
      this.$emit("expand");
    },
    onFormulaChange() {
      this.$emit("formula:change");
    },
  },
  computed: {
    mutableValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("updateValue", val);
      },
    },
    isFields() {
      return this.item && Array.isArray(this.item.fields);
    },
    isAdvanced() {
      return this.mutableValue && this.mutableValue.hasOwnProperty("formula");
    },
  },
  mounted() {
    if (this.isFields && !this.mutableValue) {
      this.toggleAdvanced();
    }
  },
};
</script>

<style lang="scss">
.fts-field-design {
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
  .field-design-label {
    flex: 1 0 240px;
    max-width: 240px;
    margin-top: 0.375rem;
    margin-right: 1rem;
    .field-design-label-name {
      color: var(--fts-text);
    }
    .field-design-label-description {
      font-size: 0.75rem;
      line-height: 1rem;
      color: var(--fts-form-helper);
    }
  }
  .field-design-control {
    flex: 1;
  }
  .field-design-form-control {
    width: 100%;
  }
  .field-design-formula {
    width: 100%;
    padding: 0.375rem 0.75rem;
    border: 1px solid transparent;
    font-size: 0.875rem;
    line-height: 1.25rem;
  }
  .field-design-checkbox {
    margin-top: 0.375rem;
    margin-bottom: 0.375rem;
  }
  &.field-design--expand {
    flex-wrap: wrap;
    > .field-design-label {
      width: 100%;
      margin-bottom: 0.25rem;
      margin-right: 0;
      flex: 1 0 100%;
      max-width: 100%;
      > .fts-form-label {
        flex: 1;
      }
    }
    > .field-design-control {
      width: 100%;
      border-radius: 0.25rem;
    }
  }
}
</style>